import React, { useEffect, useRef, useState } from 'react';
import { ComposedChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TimelineChartProps } from '../../../types/reports';
import { findPercentile, getValueForPercentile } from '../../../utils/reports';
import { Box } from '@mui/material';
import { ABOVE_RANGE, BELOW_RANGE, INCONCLUSIVE, NOT_DETECTED } from '../../../constants/report';
import { ResultTimelineCompoundTooltip } from '../elements/ResultTimelineCompoundTooltip';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';

interface CustomDoubleLabelProps {
  viewBox: {
    x: number;
    y: number;
    width: number;
  };
  valueLeft: string;
  valueRight: string;
}

const CHART_HEIGHT = 400;

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const lineData = payload.find((item: { dataKey: string }) => item.dataKey === 'compoundPercent');

    if (lineData) {
      const data = lineData.payload;

      return <ResultTimelineCompoundTooltip compoundData={data} />;
    }
  }
  return null;
};

const CompoundInNHANESTimelineChart: React.FC<TimelineChartProps> = ({
  graphData,
  allResults,
  selectedCompound,
  compoundMetaData,
}) => {
  const { theme } = useThemeContext();
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(0);

  useEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        setChartWidth(chartRef.current.offsetWidth || 0);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const ICONS = {
    INCONCLUSIVE: theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.images.inconclusive,
    NOT_DETECTED: theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.images.notDetected,
    ABOVE_RANGE: theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.images.aboveRange,
    BELOW_RANGE: theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.images.belowRange,
    BEYOND_REPORTABLE_RANGE:
      theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.images.beyondReportableRange,
  };

  const CustomDoubleLabel = ({ viewBox, valueLeft, valueRight }: CustomDoubleLabelProps) => {
    const { x, y, width } = viewBox;
    return (
      <>
        <text
          x={x + 15}
          y={y + 4}
          textAnchor="end"
          style={{
            fill: theme.custom.report.timeline.compoundInNHANESTimelineChart.customDoubleLabel.left.color,
            fontSize: 14,
            fontWeight: 400,
          }}
        >
          {valueLeft}
        </text>
        <text
          x={x + width - 50}
          y={y + 4}
          textAnchor="start"
          style={{
            fill: theme.custom.report.timeline.compoundInNHANESTimelineChart.customDoubleLabel.right.color,
            fontSize: 14,
            fontWeight: 400,
          }}
        >
          {valueRight}
        </text>
      </>
    );
  };

  const loq =
    selectedCompound && compoundMetaData?.[selectedCompound]?.loq ? compoundMetaData[selectedCompound].loq : '---';

  const detectionFrequency =
    selectedCompound && compoundMetaData?.[selectedCompound]?.detection_freq
      ? compoundMetaData[selectedCompound].detection_freq
      : '---';

  let chartData = allResults
    .map((report) => {
      const selectedCompoundData = report.compoundDataWithDdfv.find(
        (compound: { ddfv: { testNameAbbr: string } }) => compound.ddfv.testNameAbbr === selectedCompound,
      );

      if (!selectedCompoundData || selectedCompoundData.ddfv.label.trim().toLowerCase().includes('unknown')) {
        return null;
      }

      // Extract the valueAsFloat if it exists; otherwise, set it to null
      let compoundValueFloat = selectedCompoundData?.ddfv.valueAsFloat ?? null;

      let compoundPercent = 0;

      let compoundValueStr = selectedCompoundData?.value ?? '';
      let compoundLabel = selectedCompoundData?.ddfv.label;
      let compoundDisplayValue = '';

      if (compoundLabel.toLowerCase().trim().includes(INCONCLUSIVE.toLowerCase())) {
        compoundDisplayValue = 'Inconclusive';
      } else if (compoundLabel.toLowerCase().trim().includes(ABOVE_RANGE.toLowerCase())) {
        compoundDisplayValue = 'Above Reportable Range';
      } else if (compoundLabel.toLowerCase().trim().includes(BELOW_RANGE.toLowerCase())) {
        compoundDisplayValue = 'Below Reportable Range';
      } else if (compoundLabel.toLowerCase().trim().replace(/\s+/g, ' ').includes(NOT_DETECTED.toLowerCase())) {
        compoundDisplayValue = 'Not Detected';
      } else {
        compoundPercent = findPercentile(compoundValueFloat, graphData);
        compoundDisplayValue = compoundValueStr;
      }

      return {
        date: new Date(report.report_date).toLocaleDateString('en-US', {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit',
        }),
        a: findPercentile(2.0, graphData),
        b: findPercentile(20.0, graphData) - findPercentile(2.0, graphData),
        c: 100 - findPercentile(20.0, graphData),
        compoundPercent: compoundPercent,
        compoundDisplayValue,
        compoundLabel,
        loq,
        detectionFrequency,
        compoundValueFloat,
      };
    })
    .filter(Boolean);

  const referenceLines = [
    { y: 15, leftLabel: '15%', rightLabel: `${getValueForPercentile(15, graphData)} ng/mL` },
    { y: 50, leftLabel: '50%', rightLabel: `${getValueForPercentile(50, graphData)} ng/mL` },
    { y: 90, leftLabel: '90%', rightLabel: `${getValueForPercentile(90, graphData)} ng/mL` },
    { y: 100, leftLabel: '100%', rightLabel: `${getValueForPercentile(100, graphData)} ng/mL` },
  ];

  const getIconForLabel = (label: string): string | null => {
    if (!label) return null;
    const normalizedLabel = label.toLowerCase().trim().replace(/\s+/g, ' ');

    if (normalizedLabel.includes(INCONCLUSIVE.toLowerCase())) return ICONS.INCONCLUSIVE;
    if (normalizedLabel.includes(NOT_DETECTED.toLowerCase())) return ICONS.NOT_DETECTED;
    if (normalizedLabel.includes(ABOVE_RANGE.toLowerCase())) return ICONS.ABOVE_RANGE;
    if (normalizedLabel.includes(BELOW_RANGE.toLowerCase())) return ICONS.BELOW_RANGE;

    return null;
  };

  const renderCustomDot = ({ cx, cy, payload, isActive = false }: any) => {
    const icon = getIconForLabel(payload.compoundLabel);
    const isAboveRange = payload.compoundLabel.toLowerCase().includes(ABOVE_RANGE.toLowerCase());

    const beyondReportableThreshold = getValueForPercentile(100, graphData);
    const isBeyondReportableRange =
      payload.compoundPercent >= 100 &&
      payload.compoundValueFloat &&
      payload.compoundValueFloat > beyondReportableThreshold;

    // render icon for beyond reportable range (For the values above 100% but not above range)
    if (isBeyondReportableRange) {
      return (
        <>
          <line
            x1={cx}
            y1={20}
            x2={cx}
            y2={CHART_HEIGHT - 50}
            stroke={
              theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.line.dot.isBeyondReportableRange
                .line.stroke.color
            }
            strokeWidth={2}
            strokeDasharray="4 4"
          />
          <image href={ICONS.BEYOND_REPORTABLE_RANGE} x={cx - 12} y={5} width="24" height="24" />
        </>
      );
    }

    // render icon for above range
    if (isAboveRange && icon) {
      return (
        <>
          <line
            x1={cx}
            y1={20}
            x2={cx}
            y2={CHART_HEIGHT - 50}
            stroke={
              theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.line.dot.isAboveRange.line.stroke
                .color
            }
            strokeWidth={2}
            strokeDasharray="4 4"
          />
          <image href={icon} x={cx - 12} y={5} width="24" height="24" />
        </>
      );
    }

    if (icon) {
      return <image href={icon} x={cx - 10} y={CHART_HEIGHT - 70} width="20" height="20" />;
    }

    // Default dot rendering for non-special cases
    return (
      <>
        <line
          x1={cx}
          y1={cy}
          x2={cx}
          y2={CHART_HEIGHT - 40}
          stroke={
            theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.line.dot.default.line.stroke.color
          }
        />
        <circle
          cx={cx}
          cy={cy}
          r={6}
          fill={
            theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.line.dot.default.circle.fill.color
          }
          stroke={
            isActive
              ? theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.line.activeDot.default.circle
                  .stroke.color
              : theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.line.dot.default.circle.stroke
                  .color
          }
          strokeWidth={1}
        />
      </>
    );
  };

  const renderCompoundTimelineGraph = () => {
    return (
      <div
        ref={chartRef}
        style={{
          position: 'relative',
          width: '100%',
          height: CHART_HEIGHT,
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 20, right: 50, left: 30, bottom: 20 }}>
            <XAxis
              dataKey="date"
              tick={{
                fontSize: 12,
                fill: theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.xAxis.tick.color,
                fontWeight: 400,
              }}
              axisLine={{
                stroke: theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.xAxis.axisLine.color,
              }}
              tickLine={{
                stroke: theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.xAxis.tickLine.color,
              }}
              padding={{ left: 80, right: 80 }}
            />
            <YAxis
              hide
              domain={[0, 100]}
              tick={{
                fontSize: 12,
                fill: theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.yAxis.tick.color,
              }}
              axisLine={{
                stroke: theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.yAxis.axisLine.color,
              }}
              tickLine={{
                stroke: theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.yAxis.tickLine.color,
              }}
            />
            <Tooltip
              cursor={false}
              content={({ active, payload }) => <CustomTooltip active={active} payload={payload} />}
            />

            {referenceLines.map((line, index) => (
              <ReferenceLine
                key={index}
                y={line.y}
                stroke={
                  theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.referenceLines.stroke.color
                }
                strokeWidth={2}
                label={
                  <CustomDoubleLabel
                    viewBox={{
                      x: -20,
                      y: (CHART_HEIGHT * (100 - line.y)) / 100,
                      width: chartWidth,
                    }}
                    valueLeft={line.leftLabel}
                    valueRight={line.rightLabel}
                  />
                }
                ifOverflow="visible"
                clipPath="inset(0px 60px 0px 30px)"
              />
            ))}

            <Line
              type="linear"
              dataKey="compoundPercent"
              stroke="none"
              activeDot={(props: any) => renderCustomDot({ ...props, isActive: true })}
              dot={renderCustomDot}
            />
            <ReferenceLine
              y={0.0}
              stroke={
                theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.referenceLines.y0.stroke.color
              }
              strokeWidth={2}
              label={{
                value: '',
                position: 'left',
                fill: theme.custom.report.timeline.compoundInNHANESTimelineChart.composedChart.referenceLines.y0.label
                  .color,
                fontSize: 14,
                fontWeight: 400,
                textAnchor: 'end',
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return <Box>{renderCompoundTimelineGraph()}</Box>;
};

export default CompoundInNHANESTimelineChart;
